import {
  Add as AddIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  EditCalendarOutlined as EditCalendarOutlinedIcon,
  ImageOutlined as ImageOutlinedIcon,
  QueryBuilderOutlined as QueryBuilderOutlinedIcon,
  VideocamOutlined as VideocamOutlinedIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import {
  AdvancedDatePicker,
  Alert,
  Button,
  Chip,
  Modal,
  PanelCard,
  theme as dsTheme,
  Tooltip,
  Typography,
} from '@sb/cc-design-system';
import dayjs from 'dayjs';
import { IComponent } from 'gcf-player/shared-utils';
import { useEffect, useRef, useState } from 'react';
import ClickPromptIcon from 'src/assets/images/ClickPromptWithRightArrow.svg';
import { EmptyProvider } from 'src/components/emptyProvider';
import { EComponentInstanceDataTarget } from 'src/components/gcfPreview/GcfPreview.interface';
import { US_DATE_SLASH_FORMAT } from 'src/constants';
import { lang } from 'src/utils';

import {
  getScheduleOverrideStatus,
  isProductLineupComponent,
} from '../../MenuBoardModificationPage.helper';
import { ISelectedSectionDetailInfo } from '../../MenuBoardModificationPage.interface';
import { ProductLineupAccordion } from '../productLineupAccordion';
import {
  formatVideoDuration,
  getOverrideDatePickerLimits,
  getVideoComponentDuration,
  isAllowedToAddOverride,
} from './OverrideLeftSideBar.helper';
import {
  IOverrideLeftSideBar,
  IScheduleCardContent,
} from './OverrideLeftSideBar.interface';
import {
  BackupListItem,
  BackupListWrapper,
  BackupName,
  ButtonContentWrapper,
  DatePickerWrapper,
  OverrideLeftSideBarContentWrapper,
  OverrideLeftSideBarHeader,
  ScheduleCardContentWrapper,
  ScheduleCardFieldItemLabelWrapper,
  ScheduleCardFieldItemValueWrapper,
  ScheduleCardFieldItemWrapper,
  ScheduleCardWrapper,
} from './OverrideLeftSideBar.styled';

// Fetches and displays the runtime for video components inside a schedule card.
const VideoDurationDisplay = ({ component }: { component: IComponent }) => {
  const [duration, setDuration] = useState<number>();

  useEffect(() => {
    let isMounted = true;

    getVideoComponentDuration(component).then((value) => {
      if (isMounted) {
        setDuration(value);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [component]);

  return (
    <Typography variant="body2">
      {duration && formatVideoDuration(duration)}
    </Typography>
  );
};
// Renders the body of each schedule/override card, including editable date.
const ScheduleCardContent = ({
  item,
  isEditableOverride,
  selectedScheduleItem,
  previewStartDate,
  products,
  selectProductKey,
  onChangeOverrideStartDate,
  onChangeOverrideEndDate,
  onSelectAccordionProduct,
  onReorderAccordionProduct,
  onAddAccordionProducts,
  onReplaceAccordionProduct,
  onRemoveAccordionProduct,
}: IScheduleCardContent) => {
  const isOverrideItem =
    item.type === EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE;
  const isEditableOverrideItem = isOverrideItem && isEditableOverride;
  const isReplacementSelected = Boolean(item.componentId);
  const isDatePickerDisabled = isEditableOverrideItem && !isReplacementSelected;
  const { startDateMin, startDateMax, endDateMin, endDateMax } =
    getOverrideDatePickerLimits(
      selectedScheduleItem,
      isEditableOverrideItem && isReplacementSelected ? item : undefined,
    );
  const showPreviewStartDateWarning =
    isEditableOverrideItem &&
    isReplacementSelected &&
    !!previewStartDate &&
    item.startDate !== previewStartDate;

  return (
    <>
      <ScheduleCardFieldItemWrapper>
        <ScheduleCardFieldItemLabelWrapper>
          <Typography variant="body2">{lang.startDate}</Typography>
        </ScheduleCardFieldItemLabelWrapper>
        <ScheduleCardFieldItemValueWrapper>
          <EditCalendarOutlinedIcon />
          {isEditableOverrideItem ? (
            <DatePickerWrapper>
              <AdvancedDatePicker
                value={item.startDate ? dayjs(item.startDate) : null}
                minDate={startDateMin}
                maxDate={startDateMax}
                format={US_DATE_SLASH_FORMAT}
                helperText={US_DATE_SLASH_FORMAT}
                showArrows={false}
                showBorder={false}
                disabled={isDatePickerDisabled}
                height={20}
                dateColor={dsTheme.palette.text.primary}
                onChange={(newDate) =>
                  onChangeOverrideStartDate?.(item.id, newDate)
                }
              />
            </DatePickerWrapper>
          ) : (
            <Typography variant="body2">{item.startDate}</Typography>
          )}
        </ScheduleCardFieldItemValueWrapper>
      </ScheduleCardFieldItemWrapper>

      <ScheduleCardFieldItemWrapper>
        <ScheduleCardFieldItemLabelWrapper>
          <Typography variant="body2">{lang.endDate}</Typography>
        </ScheduleCardFieldItemLabelWrapper>
        <ScheduleCardFieldItemValueWrapper>
          <EditCalendarOutlinedIcon />
          {isEditableOverrideItem ? (
            <DatePickerWrapper>
              <AdvancedDatePicker
                value={item.endDate ? dayjs(item.endDate) : null}
                minDate={endDateMin}
                maxDate={endDateMax}
                format={US_DATE_SLASH_FORMAT}
                helperText={US_DATE_SLASH_FORMAT}
                showArrows={false}
                showBorder={false}
                disabled={isDatePickerDisabled}
                height={20}
                dateColor={dsTheme.palette.text.primary}
                onChange={(newDate) =>
                  onChangeOverrideEndDate?.(item.id, newDate)
                }
              />
            </DatePickerWrapper>
          ) : (
            <Typography variant="body2">{item.endDate}</Typography>
          )}
        </ScheduleCardFieldItemValueWrapper>
      </ScheduleCardFieldItemWrapper>

      {item.isVideoComponent && item.component && (
        <ScheduleCardFieldItemWrapper>
          <ScheduleCardFieldItemLabelWrapper>
            <Typography variant="body2">
              {lang.scheduleComponent.preview.duration}
            </Typography>
          </ScheduleCardFieldItemLabelWrapper>
          <ScheduleCardFieldItemValueWrapper>
            <QueryBuilderOutlinedIcon />
            <VideoDurationDisplay component={item.component} />
          </ScheduleCardFieldItemValueWrapper>
        </ScheduleCardFieldItemWrapper>
      )}

      {item.type !== EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE && (
        <>
          <ScheduleCardFieldItemWrapper>
            <ScheduleCardFieldItemLabelWrapper>
              <Typography variant="body2">{lang.modifiedDate}</Typography>
            </ScheduleCardFieldItemLabelWrapper>
            <ScheduleCardFieldItemValueWrapper>
              <EditCalendarOutlinedIcon />
              <Typography variant="body2">{item.modifiedAt}</Typography>
            </ScheduleCardFieldItemValueWrapper>
          </ScheduleCardFieldItemWrapper>
          {!!item.backups?.length && (
            <ScheduleCardFieldItemWrapper $fullWidth>
              <ScheduleCardFieldItemLabelWrapper>
                <Typography variant="body2">{lang.backup}</Typography>
              </ScheduleCardFieldItemLabelWrapper>
              <ScheduleCardFieldItemValueWrapper>
                <BackupListWrapper>
                  {item.backups.map((backup, index) => {
                    const backupName = backup?.component?.name || '-';
                    const backupKey =
                      backup?.id && backup?.componentId
                        ? `${backup.id}-${backup.componentId}`
                        : `${item.id}-backup-${index}`;

                    return (
                      <BackupListItem key={backupKey}>
                        <Tooltip showOnOverflowOnly title={backupName}>
                          <BackupName variant="body2">{backupName}</BackupName>
                        </Tooltip>
                      </BackupListItem>
                    );
                  })}
                </BackupListWrapper>
              </ScheduleCardFieldItemValueWrapper>
            </ScheduleCardFieldItemWrapper>
          )}
        </>
      )}

      {isProductLineupComponent(item) && getScheduleOverrideStatus(item.id) && (
        <ProductLineupAccordion
          overrideId={item.id}
          component={item.component}
          products={products}
          selectProductKey={selectProductKey}
          onSelectAccordionProduct={onSelectAccordionProduct}
          onReorderAccordionProduct={onReorderAccordionProduct}
          onAddAccordionProducts={onAddAccordionProducts}
          onReplaceAccordionProduct={onReplaceAccordionProduct}
          onRemoveAccordionProduct={onRemoveAccordionProduct}
        />
      )}

      {showPreviewStartDateWarning && (
        <Alert
          severity="warning"
          description={lang.scheduleComponent.preview.previewStartDateMismatch}
          dismissible
        />
      )}
    </>
  );
};

export const OverrideLeftSideBar = ({
  selectedSectionDetailInfo,
  selectedScheduleItem,
  overrideChangeSetItems = [],
  hasReachedLocalOverrideLimit = false,
  isAddOverrideDisabled = false,
  overrideCardIdToScroll,
  previewStartDate,
  products,
  selectProductKey,
  onAddOverride,
  onChangeOverrideStartDate,
  onChangeOverrideEndDate,
  onDeleteOverride,
  onDeleteExistingOverride,
  onOverrideCardScrollComplete,
  onSelectAccordionProduct,
  onAddAccordionProducts,
  onReplaceAccordionProduct,
  onRemoveAccordionProduct,
  onReorderAccordionProduct,
}: IOverrideLeftSideBar) => {
  const hasDraftOverride = overrideChangeSetItems.some((item) => item.isDraft);
  const [isOverrideLimitModalOpen, setIsOverrideLimitModalOpen] =
    useState(false);
  const [existingOverrideToDelete, setExistingOverrideToDelete] =
    useState<ISelectedSectionDetailInfo | null>(null);
  const overrideCardRef = useRef<HTMLDivElement | null>(null);
  const displayedSectionDetailInfo = [
    ...overrideChangeSetItems,
    ...selectedSectionDetailInfo,
  ];
  const handleAddOverrideClick = () => {
    if (hasReachedLocalOverrideLimit) {
      setIsOverrideLimitModalOpen(true);

      return;
    }

    onAddOverride?.();
  };
  const handleCloseLocalOverrideLimitModal = () => {
    setIsOverrideLimitModalOpen(false);
  };
  const handleCloseExistingOverrideDeleteModal = () => {
    setExistingOverrideToDelete(null);
  };
  const handleConfirmExistingOverrideDelete = () => {
    if (existingOverrideToDelete) {
      onDeleteExistingOverride?.(existingOverrideToDelete);
    }

    handleCloseExistingOverrideDeleteModal();
  };

  useEffect(() => {
    if (overrideCardIdToScroll === undefined) {
      return;
    }

    const overrideCardElement = overrideCardRef.current;

    if (!overrideCardElement) {
      return;
    }

    overrideCardElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    onOverrideCardScrollComplete?.();
  }, [
    overrideChangeSetItems,
    selectedSectionDetailInfo,
    onOverrideCardScrollComplete,
    overrideCardIdToScroll,
  ]);

  return (
    <>
      <OverrideLeftSideBarHeader variant="body2">
        {lang.components}
      </OverrideLeftSideBarHeader>
      <OverrideLeftSideBarContentWrapper>
        <EmptyProvider
          dataToCheck={selectedSectionDetailInfo}
          image={ClickPromptIcon}
          imageWidth={245}
          imageHeight={223}
          message={lang.scheduleComponent.preview.noSelectedSectionAvailable}>
          <>
            <Button
              variant="outlined-secondary-1"
              fullWidth
              size="small"
              disabled={
                !isAllowedToAddOverride(selectedSectionDetailInfo) ||
                hasDraftOverride ||
                isAddOverrideDisabled
              }
              onClick={handleAddOverrideClick}>
              <ButtonContentWrapper>
                <AddIcon />
                {lang.scheduleComponent.preview.addOverride}
              </ButtonContentWrapper>
            </Button>
            <ScheduleCardWrapper>
              {displayedSectionDetailInfo.map((item) => {
                const isEditableOverride = overrideChangeSetItems.some(
                  (overrideItem) => String(overrideItem.id) === String(item.id),
                );
                const displayTitle = item.isDraft
                  ? lang.scheduleComponent.preview.selectPromo
                  : item.component?.name ||
                    selectedScheduleItem?.component?.name;

                return (
                  <Box
                    key={item.id}
                    ref={
                      String(item.id) === String(overrideCardIdToScroll)
                        ? overrideCardRef
                        : null
                    }>
                    <PanelCard
                      title={displayTitle}
                      titleColor={
                        item.isDraft ? dsTheme.palette.error.main : undefined
                      }
                      leftIcon={
                        item.isVideoComponent ? (
                          <VideocamOutlinedIcon />
                        ) : (
                          <ImageOutlinedIcon />
                        )
                      }
                      rightIcon={
                        isEditableOverride ? (
                          <DeleteOutlinedIcon
                            sx={{ cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              onDeleteOverride?.(item.id);
                            }}
                          />
                        ) : item.type ===
                          EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE ? (
                          <DeleteOutlinedIcon
                            sx={{ cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExistingOverrideToDelete(item);
                            }}
                          />
                        ) : (
                          <Chip
                            label={lang.default}
                            chipBackgroundColor={
                              dsTheme.palette.primary.contrastText
                            }
                          />
                        )
                      }>
                      <ScheduleCardContentWrapper
                        $isDraftProductLineup={
                          isProductLineupComponent(item) &&
                          getScheduleOverrideStatus(item.id)
                        }>
                        <ScheduleCardContent
                          item={item}
                          isEditableOverride={isEditableOverride}
                          selectedScheduleItem={selectedScheduleItem}
                          previewStartDate={previewStartDate}
                          products={products}
                          selectProductKey={selectProductKey}
                          onChangeOverrideStartDate={onChangeOverrideStartDate}
                          onChangeOverrideEndDate={onChangeOverrideEndDate}
                          onSelectAccordionProduct={onSelectAccordionProduct}
                          onReorderAccordionProduct={onReorderAccordionProduct}
                          onAddAccordionProducts={onAddAccordionProducts}
                          onReplaceAccordionProduct={onReplaceAccordionProduct}
                          onRemoveAccordionProduct={onRemoveAccordionProduct}
                        />
                      </ScheduleCardContentWrapper>
                    </PanelCard>
                  </Box>
                );
              })}
            </ScheduleCardWrapper>
            <Modal
              open={isOverrideLimitModalOpen}
              title={lang.scheduleComponent.preview.maxOverrideModaltitle}
              content={lang.scheduleComponent.preview.maxOverrideModalcontent}
              onClose={handleCloseLocalOverrideLimitModal}
              onConfirmAction={handleCloseLocalOverrideLimitModal}
              confirmButtonText={lang.close}
              showCancelButton={false}
              sizeVariant="medium"
            />
            <Modal
              open={!!existingOverrideToDelete}
              title={
                lang.scheduleComponent.preview.deleteExistingOverrideModalTitle
              }
              content={
                <Typography variant="body2">
                  {
                    lang.scheduleComponent.preview
                      .deleteExistingOverrideModalContent
                  }
                </Typography>
              }
              onClose={handleCloseExistingOverrideDeleteModal}
              onCancelAction={handleCloseExistingOverrideDeleteModal}
              onConfirmAction={handleConfirmExistingOverrideDelete}
              cancelButtonText={lang.cancel}
              confirmButtonText={lang.delete}
              sizeVariant="medium"
            />
          </>
        </EmptyProvider>
      </OverrideLeftSideBarContentWrapper>
    </>
  );
};
